import { calculateD2, calculateTimeToExpirationInYears } from './BlackScholes';
import { normcdf, isCall, isPut, isLong } from './utils';

export interface SingleLegParams {
  spot: number;
  strike: number;
  premium: number;
  type: 'call' | 'put';
  numberOfContracts?: number;
  volatility?: number;
  riskFreeRate?: number;
  expirationDateString?: string | Date;
  timeToExpirationInYears?: number;
}

export interface SingleLegMetrics {
  maxProfit: number | 'Infinite';
  maxLoss: number | 'Infinite';
  probabilityOfProfit: number;
  breakEvenPrice: number;
}

/**
 * Calculates deterministic metrics for a single-leg option position
 * For long calls/puts only (quantity +1). For short positions, use negative numberOfContracts.
 *
 * @param params - Single leg option parameters
 * @returns Metrics including max profit, max loss, probability of profit, and breakeven price
 */
export function calculateSingleLegMetrics(params: SingleLegParams): SingleLegMetrics {
  const {
    spot,
    strike,
    premium,
    type,
    numberOfContracts = 1,
    volatility = 0.3, // Default volatility if not provided
    riskFreeRate = 0.05, // Default risk-free rate if not provided
    expirationDateString,
    timeToExpirationInYears,
  } = params;

  const isLongPosition = isLong(numberOfContracts);
  const contracts = Math.abs(numberOfContracts);

  // Calculate time to expiration
  let tte: number;
  if (timeToExpirationInYears !== undefined) {
    tte = timeToExpirationInYears;
  } else if (expirationDateString) {
    tte = calculateTimeToExpirationInYears(expirationDateString);
  } else {
    // Default to 30 days if no expiration provided
    tte = 30 / 365.25;
  }

  // Calculate breakeven price
  let breakEvenPrice: number;
  if (isCall(type)) {
    breakEvenPrice = strike + premium;
  } else if (isPut(type)) {
    breakEvenPrice = strike - premium;
  } else {
    throw new Error('Option type must be either call or put');
  }

  // Calculate max profit and max loss
  let maxProfit: number | 'Infinite';
  let maxLoss: number | 'Infinite';

  if (isCall(type)) {
    if (isLongPosition) {
      // Long Call: Max Loss = Premium, Max Profit = Infinite
      maxProfit = 'Infinite';
      maxLoss = premium * contracts * 100;
    } else {
      // Short Call: Max Loss = Infinite, Max Profit = Premium
      maxProfit = premium * contracts * 100;
      maxLoss = 'Infinite';
    }
  } else if (isPut(type)) {
    if (isLongPosition) {
      // Long Put: Max Loss = Premium, Max Profit = Strike - Premium (stock goes to 0)
      maxProfit = (strike - premium) * contracts * 100;
      maxLoss = premium * contracts * 100;
    } else {
      // Short Put: Max Loss = Strike - Premium (stock goes to 0), Max Profit = Premium
      maxProfit = premium * contracts * 100;
      maxLoss = (strike - premium) * contracts * 100;
    }
  } else {
    throw new Error('Option type must be either call or put');
  }

  // Calculate probability of profit using Black-Scholes
  const probabilityOfProfit = calculateProbabilityOfProfit({
    spot,
    strike,
    premium,
    type,
    volatility,
    riskFreeRate,
    timeToExpirationInYears: tte,
    isLongPosition,
  });

  return {
    maxProfit,
    maxLoss,
    probabilityOfProfit,
    breakEvenPrice,
  };
}

/**
 * Calculates probability of profit for a single-leg option
 * Uses Black-Scholes model with d2 for probability calculations
 */
function calculateProbabilityOfProfit({
  spot,
  strike,
  premium,
  type,
  volatility,
  riskFreeRate,
  timeToExpirationInYears,
  isLongPosition,
}: {
  spot: number;
  strike: number;
  premium: number;
  type: 'call' | 'put';
  volatility: number;
  riskFreeRate: number;
  timeToExpirationInYears: number;
  isLongPosition: boolean;
}): number {
  // Only a non-positive tte gets the settle-now binary (division-by-zero guard for
  // direct callers). Any positive tte — even the 1e-10 clamp for expired options —
  // must flow through N(d2), which converges to the same binary as T→0. A wider
  // threshold here is wrong: 0.001y is ~8.8 hours, which turned every intraday
  // 0DTE into a hard 0/1 instead of a real probability.
  if (timeToExpirationInYears <= 0) {
    let probability: number;
    if (isCall(type)) {
      // For a call, we profit if spot > breakeven (strike + premium)
      probability = spot > strike + premium ? 1 : 0;
    } else {
      // For a put, we profit if spot < breakeven (strike - premium)
      probability = spot < strike - premium ? 1 : 0;
    }

    // Invert for short positions
    if (!isLongPosition) {
      probability = 1 - probability;
    }

    return probability;
  }

  // Calculate the breakeven price
  let breakEven: number;
  if (isCall(type)) {
    breakEven = strike + premium;
  } else {
    breakEven = strike - premium;
  }

  // Use d2 from Black-Scholes to estimate probability
  // d2 represents the probability that the option will expire in the money
  // We need to adjust for the breakeven, not just the strike
  const d2 = calculateD2({
    spot,
    strike: breakEven, // Use breakeven as the effective strike for probability
    timeToExpirationInYears,
    riskFreeRate,
    volatility,
    type,
    position: isLongPosition ? 'long' : 'short',
    isCall: isCall(type),
    isPut: isPut(type),
    isLong: isLongPosition,
    isShort: !isLongPosition,
  });

  let probability: number;
  if (isCall(type)) {
    // For a call, we profit if spot > breakeven at expiration
    probability = normcdf(d2);
  } else {
    // For a put, we profit if spot < breakeven at expiration
    probability = normcdf(-d2);
  }

  // Short positions have inverse probability
  if (!isLongPosition) {
    probability = 1 - probability;
  }

  // Return as percentage
  return probability;
}
